export const en = {
  // Navigation
  nav: {
    home: "Home",
    about: "About",
    skills: "Skills",
    projects: "Projects",
    experience: "Experience",
    contact: "Contact",
    portfolio: "Portfolio",
    light: "Light",
    dark: "Dark",
    english: "English",
    arabic: "Arabic",
    french: "French",
  },

  loading: {
    message: "Loading portfolio…",
  },

  // Hero Section
  hero: {
    title: "Mokhles Lajimi",
    eyebrow: "Hi, I'm",
    subtitle: "Full Stack Mobile & Web Developer",
    availableBadge: "Available for Freelance",
    recentLaunches: "Recent launches",
    metricYears: "Years exp.",
    metricProjects: "Projects",
    metricStores: "Play & App Store",
    description:
      "Full Stack Mobile Developer with 5+ years of experience building high-performance Flutter apps and scalable backends. I focus on smooth UX, reliable data flows, and production-ready delivery across Android, iOS, and the web.",
    downloadCV: "Download CV",
    contactWhatsApp: "Contact via WhatsApp",
  },

  // About Section
  about: {
    title: "About Me",
    description1:
      "I'm a passionate developer with 5+ years of experience creating digital solutions that make a difference. I specialize in Flutter mobile development and love turning complex problems into simple, beautiful designs.",
    description2:
      "When I'm not coding, you can find me exploring new technologies, contributing to open source projects, or sharing my knowledge with the developer community. I'm always eager to learn and grow in this ever-evolving field.",
    stats: [
      { number: "18+", label: "Projects Completed" },
      { number: "5+", label: "Years Experience" },
      { number: "100%", label: "Client Satisfaction" },
    ],
  },

  // Skills Section
  skills: {
    title: "Skills",
    subtitle: "What I build with",
    categories: [
      {
        category: "Mobile Development",
        icon: "📱",
        description: "Cross-platform mobile solutions",
        skills: [
          { name: "Flutter", level: 92, color: "#02569B" },
          { name: "Dart", level: 88, color: "#0175C2" },
          { name: "Kotlin", level: 72, color: "#7F52FF" },
          { name: "Swift", level: 68, color: "#FA7343" },
          { name: "Android", level: 82, color: "#3DDC84" },
          { name: "iOS", level: 78, color: "#000000" },
        ],
      },
      {
        category: "Frontend Development",
        icon: "💻",
        description: "Modern web interfaces",
        skills: [
          { name: "React.js", level: 88, color: "#61DAFB" },
          { name: "TypeScript", level: 82, color: "#3178C6" },
          { name: "Next.js", level: 75, color: "#000000" },
          { name: "Tailwind CSS", level: 85, color: "#06B6D4" },
          { name: "HTML5", level: 90, color: "#E34F26" },
          { name: "CSS3", level: 88, color: "#1572B6" },
        ],
      },
      {
        category: "Backend Development",
        icon: "🔧",
        description: "Robust server solutions",
        skills: [
          { name: "Node.js", level: 85, color: "#339933" },
          { name: "Express", level: 82, color: "#000000" },
          { name: "Python", level: 80, color: "#3776AB" },
          { name: "Flask", level: 75, color: "#000000" },
          { name: "REST APIs", level: 88, color: "#FF6B6B" },
          { name: "GraphQL", level: 70, color: "#E10098" },
          { name: "WebSockets", level: 78, color: "#010101" },
        ],
      },
      {
        category: "Data & Cloud",
        icon: "☁️",
        description: "Scalable data solutions",
        skills: [
          { name: "MongoDB", level: 82, color: "#47A248" },
          { name: "PostgreSQL", level: 78, color: "#336791" },
          { name: "Firebase", level: 85, color: "#FFCA28" },
          { name: "Retool", level: 70, color: "#FF6B35" },
          { name: "CI/CD", level: 72, color: "#2088FF" },
          { name: "Docker", level: 68, color: "#2496ED" },
          { name: "Git", level: 90, color: "#F05032" },
        ],
      },
    ],
    additionalExpertise: "Also",
    additionalSubtitle: "",
    additionalSkills: [
      "Agile/Scrum",
      "UI/UX Design",
      "API Testing",
      "Performance Optimization",
      "State Management",
      "Responsive Design",
      "Version Control",
      "Code Review",
      "Testing & QA",
      "Project Management",
      "Client Communication",
      "Technical Writing",
    ],
  },

  // Projects Section
  projects: {
    title: "Projects",
    subtitle: "Shipped to Play Store, App Store & web",
    countLabel: "projects",
    seeMore: "See more",
    seeLess: "Show less",
    categories: [
      { id: "all", name: "All Projects", icon: "fas fa-th" },
      { id: "mobile", name: "Mobile Apps", icon: "fas fa-mobile-alt" },
      { id: "web", name: "Web Apps", icon: "fas fa-globe" },
      { id: "game", name: "Games", icon: "fas fa-gamepad" },
    ],
    items: [
      {
        title: "Click Services",
        description:
          "Multilingual services marketplace (15+ languages): real-time chat, bookings, wallet & cashback, secure payments.",
        icon: "🛎️",
        category: "mobile",
        status: "Live",
        users: "10K+",
        rating: "4.8",
        tech: [
          "Flutter",
          "Node.js",
          "MongoDB",
          "Stripe",
          "PayPal",
          "Socket.IO",
          "OneSignal",
        ],
        features: [
          "Real-time Chat",
          "Multi-language",
          "Secure Payments",
          "Wallet System",
        ],
        links: [
          {
            text: "Play Store",
            url: "https://play.google.com/store/apps/details?id=com.ngtech.cliickservice",
            icon: "fab fa-google-play",
            type: "primary",
          },
          {
            text: "App Store",
            url: "https://apps.apple.com/us/app/click-services/id6744241291",
            icon: "fab fa-app-store-ios",
            type: "primary",
          },
        ],
      },
      {
        title: "Ritchess",
        description:
          "Ride-hailing suite (client, driver, admin): booking, live tracking, reviews, zones, payments, notifications.",
        icon: "🚗",
        category: "mobile",
        status: "Live",
        users: "5K+",
        rating: "4.6",
        tech: [
          "Flutter",
          "Node.js",
          "Socket.IO",
          "MongoDB",
          "Provider",
          "GetX",
        ],
        features: [
          "Live Tracking",
          "Multi-role App",
          "Payment Integration",
          "Real-time Updates",
        ],
        links: [
          {
            text: "Client App",
            url: "https://play.google.com/store/apps/details?id=com.cinnov.ritchess",
            icon: "fab fa-google-play",
            type: "primary",
          },
        ],
      },
      {
        title: "Portfolio Web",
        description:
          "Personal responsive portfolio with dark mode, smooth animations and modern design.",
        icon: "💼",
        category: "web",
        status: "Live",
        users: "1K+",
        rating: "5.0",
        tech: ["React", "CSS3", "JavaScript", "Responsive Design"],
        features: [
          "Responsive Design",
          "Dark Mode",
          "Animations",
          "Performance Optimized",
        ],
        links: [
          {
            text: "View Site",
            url: "#",
            icon: "fas fa-external-link-alt",
            type: "primary",
          },
          {
            text: "Source Code",
            url: "#",
            icon: "fab fa-github",
            type: "secondary",
          },
        ],
      },
    ],
  },

  // Experience Section
  experience: {
    title: "Experience",
    subtitle: "Growth, ownership, and impact across mobile & web",
    items: [
      {
        period: "09/2025 - Present",
        title: "Mobile Flutter Developer",
        company: "NexBuild, Tunisie",
        description:
          "Leading the delivery of cross-platform mobile apps with Flutter, from UI/UX implementation based on design mockups to store releases on Google Play and the App Store. Owning project planning and technical specs, and integrating secure, well-tested backend APIs using tools like Postman and Swagger.",
      },
      {
        period: "01/2024 - 09/2025",
        title: "Senior Flutter Developer | Full Stack Web Developer",
        company: "Converge Innova, Tunisie",
        description:
          "Owning end-to-end delivery of cross-platform products: architecting Flutter apps and Node.js/MongoDB backends, shipping to production, and monitoring performance and reliability. Collaborating with product and design in a Scrum team, writing clear technical specs, and validating APIs with Postman and Swagger before integrating them in mobile clients.",
      },
      {
        period: "02/2022 – 01/2024",
        title: "Flutter & Backend Developer",
        company: "3D Wave Sousse, Tunisie",
        description:
          "Delivered production Flutter apps for Android and iOS, from feature design to release and maintenance. Built and integrated REST APIs with Node.js and Flask, worked closely with designers and PMs in Agile sprints, and iterated quickly on new features based on client feedback.",
      },
      {
        period: "02/2021 – 11/2023",
        title: "Full Stack Developer",
        company: "Lonch (FreeLance)",
        description:
          "Designed and implemented internal tools and dashboards with Retool, and created reusable Flutter packages to speed up future projects. Integrated modern frontends with GraphQL/REST backends, and managed delivery and communication directly with stakeholders as a freelance full stack developer.",
      }
    ],
  },

  // Contact Section
  contact: {
    title: "Let's talk",
    subtitle: "Have an idea? I'll help you ship it.",
    methodWhatsApp: "Quick reply",
    methodEmail: "Email",
    methodLinkedIn: "LinkedIn",
    methods: [
      {
        name: "WhatsApp",
        url: "https://wa.me/21629060392",
        icon: "fab fa-whatsapp",
      },
      {
        name: "Email",
        url: "mailto:mokhleslajimi@gmail.com",
        icon: "fas fa-envelope",
      },
      {
        name: "LinkedIn",
        url: "https://www.linkedin.com/in/mokhleslajimi/",
        icon: "fab fa-linkedin",
      },
    ],
    form: {
      action: "https://formsubmit.co/mokhleslajimi@gmail.com",
      fields: [
        { label: "Your name", placeholder: "Jane Doe" },
        { label: "Email", placeholder: "jane@example.com" },
        { label: "Company (optional)", placeholder: "Acme Inc." },
        {
          label: "Project details",
          placeholder: "What are you building?",
        },
      ],
      hint: "Reply within 24–48h.",
      submitText: "Send message",
    },
  },

  // Footer
  footer: {
    description:
      "Full-stack mobile & web developer shipping real products.",
    socialLinks: [
      {
        name: "GitHub",
        url: "https://github.com/mokhleslajimi",
        icon: "fab fa-github",
      },
      {
        name: "LinkedIn",
        url: "https://www.linkedin.com/in/mokhleslajimi/",
        icon: "fab fa-linkedin",
      },
      {
        name: "WhatsApp",
        url: "https://wa.me/21629060392",
        icon: "fab fa-whatsapp",
      },
    ],
    quickLinks: [
      { name: "Skills", action: "scroll", target: "skills" },
      { name: "Projects", action: "scroll", target: "projects" },
      { name: "Experience", action: "scroll", target: "experience" },
      {
        name: "Download CV",
        action: "link",
        target: "dynamic_cv",
      },
    ],
    services: [
      "Mobile App Development",
      "Web Development",
      "Backend Development",
      "UI/UX Consulting",
      "Technical Consulting",
    ],
    copyright: "© 2025 Mokhles Lajimi. All rights reserved.",
    quickLinksTitle: "Quick Links",
    servicesTitle: "Services",
  },
};
